/**
 * HeaderOverflowMenu - Menu de overflow para o header mobile
 * 
 * Agrupa itens secundários do header (ModuleSwitcher, Status, Tema, Idioma, etc.)
 * num Popover acionado por botão "⋮", evitando quebra de layout no mobile.
 * 
 * Visível apenas em telas < md (768px).
 * 
 * @version 2.0.0
 * RELEASE: 2026-08-20 v2.0.0 - Removida a listagem legada de módulos (ModuleSwitcher).
 *          A navegação por módulos agora é feita exclusivamente pelo menu lateral.
 *          O menu passa a exibir apenas ações (inboxes desconectadas + tema).
 * RELEASE: 2026-03-20 v1.0.0 - Criação do componente
 */

import { MoreVertical } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

import { ThemeToggle } from '@/components/ui/theme-toggle';

import { DisconnectedInboxesIndicator } from '@/components/tenant/inbox/DisconnectedInboxesIndicator';
import { ModuleType } from '@/hooks/useActiveModule';

interface HeaderOverflowMenuProps {
  /** Módulo atualmente ativo */
  activeModule: ModuleType;
  /** Callback para alternar módulo */
  onSwitchModule: (module: ModuleType) => void;
  /** Se o módulo CRM está disponível */
  isCrmAvailable: boolean;
  /** Se o módulo Financeiro está disponível */
  isFinancialAvailable: boolean;
  /** Se o módulo Tarefas está disponível */
  isTasksAvailable?: boolean;
  /** Se o módulo Indicação está disponível (Fase 5 — Bloco 5A). Reservado p/ uso futuro. */
  isReferralAvailable?: boolean;
  /** Se o novo módulo Atividades está disponível (Fase 3 — Playbook de Vendas). */
  isActivitiesAvailable?: boolean;
  /** Se o módulo Wiki / Base de Conhecimento está disponível */
  isKnowledgeAvailable?: boolean;
  /** 
   * Variante do menu:
   * - 'full': mostra módulos + status + idioma + tema (mobile < md)
   * - 'actions-only': mostra apenas status + idioma + tema (desktop compacto md–xl)
   */
  variant?: 'full' | 'actions-only';
}

/**
 * Menu de overflow do header.
 * - variant='full': ModuleSwitcher + inboxes + status + tema + idioma (mobile)
 * - variant='actions-only': apenas status + tema + idioma (desktop compacto)
 */
export function HeaderOverflowMenu({
  activeModule,
  onSwitchModule,
  isCrmAvailable,
  isFinancialAvailable,
  isTasksAvailable = false,
  isReferralAvailable = false,
  isActivitiesAvailable = false,
  isKnowledgeAvailable = false,
  variant = 'full',
}: HeaderOverflowMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full"
          aria-label="Menu de opções"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-64 p-3 space-y-3"
      >
        {/* Indicador de Inboxes Desconectadas */}
        <DisconnectedInboxesIndicator />

        <Separator />

        {/* Toggle de Tema */}
        <div className="flex items-center gap-2">
          <ThemeToggle />
        </div>
      </PopoverContent>
    </Popover>
  );
}
